/**
 * Three.js Line that renders a trajectory preview from sampled points.
 * Wraps src/game/trajectory.ts (pure sampler) into a scene-attached
 * visual the slingshot drag can update each frame.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Line,
  LineBasicMaterial,
  type Object3D,
  type Scene,
} from "three";

import type { SamplePoint } from "../game/trajectory.ts";

export interface TrajectoryLine {
  object: Object3D;
  setPoints(points: ReadonlyArray<SamplePoint>): void;
  setVisible(visible: boolean): void;
  detach(): void;
}

export interface TrajectoryLineOptions {
  scene: Scene;
  maxPoints?: number;
  color?: number;
}

const DEFAULT_MAX_POINTS = 64;

export function createTrajectoryLine(
  options: TrajectoryLineOptions,
): TrajectoryLine {
  const maxPoints = options.maxPoints ?? DEFAULT_MAX_POINTS;
  const positions = new Float32Array(maxPoints * 3);
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setDrawRange(0, 0);
  const material = new LineBasicMaterial({
    color: options.color ?? 0xfff4c2,
    transparent: true,
    opacity: 0.65,
  });
  const line = new Line(geometry, material);
  line.name = "trajectory-line";
  line.visible = false;
  options.scene.add(line);

  const setPoints = (points: ReadonlyArray<SamplePoint>): void => {
    const n = Math.min(points.length, maxPoints);
    for (let i = 0; i < n; i += 1) {
      const offset = i * 3;
      const p = points[i]!;
      positions[offset] = p.x;
      positions[offset + 1] = p.y;
      positions[offset + 2] = p.z;
    }
    geometry.setDrawRange(0, n);
    geometry.attributes["position"]!.needsUpdate = true;
  };

  const setVisible = (visible: boolean): void => {
    line.visible = visible;
  };

  const detach = (): void => {
    geometry.dispose();
    material.dispose();
    options.scene.remove(line);
  };

  return { object: line, setPoints, setVisible, detach };
}

export const __testing = { DEFAULT_MAX_POINTS };
